import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  address?: string;
  city?: string;
  district?: string;
  postalCode?: string;
  companyName?: string;
  taxNumber?: string;
  taxOffice?: string;
  createdAt: string;
  updatedAt: string;
}

interface AuthState {
  user: User | null;
  users: User[];
  isAuthenticated: boolean;

  register: (data: Omit<User, 'id' | 'createdAt' | 'updatedAt'>) => { success: boolean; error?: string };
  login: (email: string, password: string) => { success: boolean; error?: string };
  logout: () => void;
  updateProfile: (updates: Partial<User>) => void;
  getUserById: (id: string) => User | undefined;
  getUserByEmail: (email: string) => User | undefined;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      users: [],
      isAuthenticated: false,

      register: (data) => {
        const existing = get().users.find(u => u.email.toLowerCase() === data.email.toLowerCase());
        if (existing) {
          return { success: false, error: 'Bu e-posta adresi zaten kayıtlı' };
        }

        const newUser: User = {
          ...data,
          id: `USR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        set((state) => ({
          users: [...state.users, newUser],
          user: newUser,
          isAuthenticated: true,
        }));

        return { success: true };
      },

      login: (email, password) => {
        const user = get().users.find(
          u => u.email.toLowerCase() === email.toLowerCase() && u.password === password
        );

        if (!user) {
          return { success: false, error: 'E-posta veya şifre hatalı' };
        }

        set({ user, isAuthenticated: true });
        return { success: true };
      },

      logout: () => {
        set({ user: null, isAuthenticated: false });
      },

      updateProfile: (updates) => {
        const currentUser = get().user;
        if (!currentUser) return;

        const updatedUser = { ...currentUser, ...updates, updatedAt: new Date().toISOString() };

        set((state) => ({
          user: updatedUser,
          users: state.users.map(u => u.id === currentUser.id ? updatedUser : u),
        }));
      },

      getUserById: (id) => get().users.find(u => u.id === id),
      getUserByEmail: (email) => get().users.find(u => u.email.toLowerCase() === email.toLowerCase()),
    }),
    {
      name: 'trax-auth',
      partialize: (state) => ({
        user: state.user,
        users: state.users,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
